/**
 * Alerting — pure helpers for the Alerts surface.
 *
 * Everything here is dependency-free (no DOM, no api calls) so it unit-tests
 * without mounting a component. The template/validation constants mirror the
 * server's (`packages/loggob/src/alert-webhook.ts` + the /api/alerts body
 * validation) so client-side errors match what the server would reject.
 */

import type { Alert, AlertOperator, MetricAgg } from '../api.js';
import { labelDisplayName } from './metric-exploration.js';

// --- Operators --------------------------------------------------------------

export const ALERT_OPERATORS: readonly AlertOperator[] = ['>', '>=', '<', '<=', '==', '!='];

/** Plain-language word for an operator ("avg of X is *above* 500"). */
export const OPERATOR_WORD: Record<AlertOperator, string> = {
  '>': 'above',
  '>=': 'at or above',
  '<': 'below',
  '<=': 'at or below',
  '==': 'exactly',
  '!=': 'anything but',
};

/** The full §2.3 aggregation set — offered when the metric's type is unknown
 * (e.g. editing an alert whose metric no longer appears in the catalog). */
export const ALL_ALERT_AGGS: readonly MetricAgg[] = [
  'avg',
  'sum',
  'min',
  'max',
  'last',
  'count',
  'rate',
  'p50',
  'p90',
  'p95',
  'p99',
];

// --- Server limits (mirror packages/loggob/src validation) ------------------

export const MAX_ALERT_NAME_LEN = 64;
export const MAX_ALERT_TEMPLATE_LEN = 1024;
export const MAX_ALERT_WEBHOOK_URL_LEN = 512;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
export const MAX_EVAL_WINDOW_MS = ONE_DAY_MS;
export const MAX_FOR_MS = 7 * ONE_DAY_MS;
export const MAX_COOLDOWN_MS = 7 * ONE_DAY_MS;
export const DEFAULT_EVAL_WINDOW_MS = 60_000;

/** Mirrors the server's Discord-webhooks-only SSRF allowlist. */
const DISCORD_WEBHOOK_URL_RE = /^https:\/\/(canary\.|ptb\.)?discord(app)?\.com\/api\/webhooks\//;
export function isDiscordWebhookUrl(url: string): boolean {
  return DISCORD_WEBHOOK_URL_RE.test(url);
}

// --- Durations ---------------------------------------------------------------

export type DurationUnit = 's' | 'm' | 'h';

export const DURATION_UNITS: Array<{ value: DurationUnit; label: string }> = [
  { value: 's', label: 'seconds' },
  { value: 'm', label: 'minutes' },
  { value: 'h', label: 'hours' },
];

const UNIT_MS: Record<DurationUnit, number> = { s: 1000, m: 60_000, h: 3_600_000 };

/** Friendly (value, unit) → milliseconds. Non-finite/negative values clamp to 0. */
export function partsToMs(value: number, unit: DurationUnit): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value * UNIT_MS[unit]);
}

/** Milliseconds → the largest friendly unit that divides cleanly, so 90000
 * round-trips as 90 s (not 1.5 m) and 120000 as 2 m. 0 becomes (0, 'm') —
 * minutes is the natural unit people reach for when they then type a value. */
export function msToParts(ms: number): { value: number; unit: DurationUnit } {
  if (!Number.isFinite(ms) || ms <= 0) return { value: 0, unit: 'm' };
  for (const unit of ['h', 'm'] as const) {
    if (ms % UNIT_MS[unit] === 0) return { value: ms / UNIT_MS[unit], unit };
  }
  return { value: Math.round(ms / 1000), unit: 's' };
}

/** Compact human duration: "30s", "2m", "1h 30m", "90s" → "1m 30s", 0 → "0s". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0s';
  const totalSeconds = Math.round(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const parts: string[] = [];
  if (h) parts.push(`${String(h)}h`);
  if (m) parts.push(`${String(m)}m`);
  if (s || !parts.length) parts.push(`${String(s)}s`);
  return parts.join(' ');
}

// --- Numbers -----------------------------------------------------------------

/** Compact numeric formatting matching the server's template interpolation
 * (integers verbatim, else rounded to 3 decimals — no 0.30000000000000004). */
export function formatAlertNumber(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'n/a';
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 1000) / 1000);
}

// --- Condition summary ---------------------------------------------------------

export interface AlertCondition {
  metric: string;
  labels: Record<string, string>;
  agg: MetricAgg;
  operator: AlertOperator;
  threshold: number;
  evalWindowMs: number;
  forMs: number;
}

/** Human `key=value, …` rendering of a label selector (reserved keys friendly). */
export function labelSummary(labels: Record<string, string>): string {
  return Object.entries(labels)
    .map(([k, v]) => `${labelDisplayName(k)}=${v}`)
    .join(', ');
}

/**
 * The one-line plain-language reading of an alert condition, e.g.
 * "avg of http_request_ms (route=/api) is above 500 for 2m".
 * The evaluation window is deliberately omitted here (it's an evaluation
 * detail the form explains); `conditionSentence` includes it.
 */
export function conditionSummary(a: AlertCondition): string {
  const labels = labelSummary(a.labels);
  const base = `${a.agg} of ${a.metric}${labels ? ` (${labels})` : ''} is ${
    OPERATOR_WORD[a.operator]
  } ${formatAlertNumber(a.threshold)}`;
  return a.forMs > 0 ? `${base} for ${formatDuration(a.forMs)}` : base;
}

/** The full sentence shown live in the form, including both duration knobs. */
export function conditionSentence(a: AlertCondition): string {
  const labels = labelSummary(a.labels);
  const hold =
    a.forMs > 0
      ? `holds continuously for ${formatDuration(a.forMs)}`
      : 'is met (fires immediately)';
  return (
    `Fires when the ${a.agg} of ${a.metric}${labels ? ` (${labels})` : ''} over the last ` +
    `${formatDuration(a.evalWindowMs)} is ${OPERATOR_WORD[a.operator]} ` +
    `${formatAlertNumber(a.threshold)} and that ${hold}.`
  );
}

// --- Message templates ----------------------------------------------------------

/** Template variables the server interpolates (§4.3). */
export const TEMPLATE_VARS = [
  'alert',
  'metric',
  'value',
  'threshold',
  'operator',
  'labels',
  'state',
  'firedAt',
] as const;

/** Mirrors the server's default fire template. */
export const DEFAULT_FIRE_TEMPLATE =
  '🔴 **{{alert}}** — {{metric}} is {{value}} ({{operator}} {{threshold}})';

/**
 * Client-side mirror of the server's `renderMessage`: interpolates `{{var}}`
 * tokens (whitespace-tolerant); unknown tokens are left verbatim so a typo'd
 * template previews visibly wrong instead of silently dropping data.
 */
export function renderTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g, (token, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : token,
  );
}

/** Sample vars for the live template preview — same shapes the server sends. */
export function sampleTemplateVars(a: {
  name: string;
  metric: string;
  labels: Record<string, string>;
  operator: AlertOperator;
  threshold: number;
}): Record<string, string> {
  return {
    alert: a.name || 'My alert',
    metric: a.metric || 'metric',
    value: formatAlertNumber(a.threshold),
    threshold: formatAlertNumber(a.threshold),
    operator: a.operator,
    labels: JSON.stringify(a.labels),
    state: 'test',
    firedAt: new Date().toISOString(),
  };
}

// --- State presentation ------------------------------------------------------------

/** LgChip tone per alert state (§4.3 colors: green ok, amber pending, red firing). */
export function stateTone(state: Alert['state']): 'ok' | 'warn' | 'danger' {
  return state === 'firing' ? 'danger' : state === 'pending' ? 'warn' : 'ok';
}

export const STATE_LABEL: Record<Alert['state'], string> = {
  ok: 'OK',
  pending: 'Pending',
  firing: 'Firing',
};
